/**
 * Carries THIS tab's account into a NEW tab opened from an in-app link.
 *
 * ── The gap this closes ─────────────────────────────────────────────────────
 * The per-tab account lives in `sessionStorage`, and a new tab does NOT reliably
 * inherit it: the spec only copies session storage when the new browsing context
 * is created FROM this one, and `rel="noopener"` (plus several browser paths for
 * ctrl+click / middle-click) opts out entirely. So ctrl+clicking an item while
 * working in account B could open it under account A — the default — which is
 * exactly the confusion the whole change set exists to remove.
 *
 * The fix is a single delegated listener: on a MODIFIED click (ctrl/cmd, or a
 * middle click) over an in-app link, we open the target ourselves with a
 * one-shot `?as=<alias>` handoff (see `lib/tab-account.ts`), which the new tab
 * consumes and strips on its first account read.
 *
 * ── Why this does not pollute URLs ──────────────────────────────────────────
 * The decoration exists only on the URL handed to `window.open`. The tab the
 * user is looking at never changes, and the NEW tab rewrites its own address bar
 * to the canonical URL within its first tick. Nothing the user can copy from a
 * settled address bar carries an account.
 *
 * ── Deliberately narrow ─────────────────────────────────────────────────────
 * Only same-origin, in-app, plain anchors are intercepted. External links,
 * downloads, explicit `target`s and shift+click (open in new WINDOW, where the
 * user is asking for a fresh context) are left to the browser untouched.
 */

import { accountHandoffUrl } from "@/lib/canonical-link";
import { readTabAccountId } from "@/lib/tab-account";

let installed = false;

/** The anchor a click landed on, or `null` when the click was not on one. */
function anchorFromEvent(event: Event): HTMLAnchorElement | null {
  const path = event.composedPath();
  for (const node of path) {
    if (node instanceof HTMLAnchorElement) {
      return node;
    }
  }
  return null;
}

/** True for a plain, same-origin, in-app anchor we may re-open ourselves. */
function isInAppAnchor(anchor: HTMLAnchorElement): boolean {
  if (anchor.target !== "" || anchor.hasAttribute("download")) {
    return false;
  }
  const href = anchor.getAttribute("href");
  if (href === null || href === "" || href.startsWith("#")) {
    return false;
  }
  try {
    return new URL(anchor.href).origin === globalThis.location.origin;
  } catch {
    return false;
  }
}

/**
 * True when this click means "open in a new TAB": ctrl (Windows/Linux), cmd
 * (macOS) or the middle button. Shift means "new window" and is left alone.
 */
function opensInNewTab(event: MouseEvent): boolean {
  if (event.shiftKey) {
    return false;
  }
  return event.ctrlKey || event.metaKey || event.button === 1;
}

function handleClick(event: MouseEvent): void {
  if (event.defaultPrevented || !opensInNewTab(event)) {
    return;
  }
  const anchor = anchorFromEvent(event);
  if (!anchor || !isInAppAnchor(anchor)) {
    return;
  }
  const userId = readTabAccountId();
  if (userId === null) {
    // Nothing to carry — let the browser do its normal thing.
    return;
  }
  event.preventDefault();
  // No `noopener`: the new tab is ours, same origin, and keeping the opener lets
  // the browser ALSO copy sessionStorage where it supports it. The `?as=`
  // handoff is what makes the outcome deterministic everywhere else.
  globalThis.open(accountHandoffUrl(anchor.href, userId), "_blank");
}

/**
 * Installs the delegated new-tab handoff. Lifetime singleton, idempotent, and a
 * no-op without a DOM.
 */
export function installNewTabAccountHandoff(): void {
  if (installed || typeof globalThis.addEventListener !== "function") {
    return;
  }
  installed = true;
  // Capture phase so we decide before the router's own link handling; `auxclick`
  // is what fires for the middle button.
  globalThis.addEventListener("click", handleClick, { capture: true });
  globalThis.addEventListener("auxclick", handleClick, { capture: true });
}
